import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Billing, billingQueryKeys, userQueryKeys } from '#api';
import { usePrefetchBillingDetailsFn } from '#api/billing.queries.ts';
import { trackError } from '#api/errors.ts';
import { CenteredLayout } from '#components/patterns/layouts/centered/CenteredLayout.tsx';
import { useUrlParams } from '#hooks/useUrlParams.ts';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';

// http://localhost:8080/billing/upgrade-success?setup_intent=seti_123456&setup_intent_client_secret=seti_123456_secret_123456&redirect_status=succeeded

const upgradeSuccessPath = '/settings/billing/upgrade-success';
const PARAM_SCHEMA = {
  setup_intent: 'scalar',
  setup_intent_client_secret: 'scalar',
  redirect_status: 'scalar',
} as const;

export function PendingUpgradePage() {
  const [{ setup_intent, setup_intent_client_secret, redirect_status }, setUrlParams] = useUrlParams(PARAM_SCHEMA);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const prefetchBillingDetails = usePrefetchBillingDetailsFn();
  const setupIntentSuccessMutation = useMutation({
    mutationFn: Billing.setupIntentSuccess,
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: billingQueryKeys.billingDetails });
      void queryClient.refetchQueries({ queryKey: userQueryKeys.anyCurrent(), type: 'active' }, { throwOnError: true });
      void prefetchBillingDetails();
      void navigate(upgradeSuccessPath, { replace: true });
      return;
    },
    onError: (error) => trackError(error),
  });

  // https://stripe.com/docs/payments/setupintents/lifecycle
  // https://stripe.com/docs/api/setup_intents/object#setup_intent_object-status
  // Status of this SetupIntent, one of requires_payment_method, requires_confirmation, requires_action, processing, canceled, or succeeded.
  // TODO: handle `redirect_status="requires_payment_details"

  // Prevent calling useEffect twice
  const effectCalled = useRef(false);

  // <https://react.dev/reference/react/useEffectEvent> avoid rerunning b/c of url params change
  const onLoad = useEffectEvent(() => {
    const clearParams = () =>
      setUrlParams({
        setup_intent: undefined,
        setup_intent_client_secret: undefined,
        redirect_status: undefined,
      });

    // Making the assumption currently that Stripe will always return each of these.
    if (!setup_intent || !setup_intent_client_secret || !redirect_status) {
      console.warn('page loaded with missing search params');
      void navigate(upgradeSuccessPath, { replace: true });
    } else {
      setupIntentSuccessMutation.mutate({
        setupIntent: setup_intent,
        setupIntentClientSecret: setup_intent_client_secret,
      });

      clearParams();
    }
  });

  useEffect(() => {
    if (!effectCalled.current) {
      effectCalled.current = true;
      onLoad();
    }
  }, []);

  return (
    <CenteredLayout>
      <LoadingSpinner />
    </CenteredLayout>
  );
}
